import moment from 'moment';
import { TreeNode } from 'primereact/treenode';

import { PersonAbsence, TimelineItem, TimelineScale } from '../../types/interfaces';

import { buildAbsenceItems, buildDepartmentBgItem, buildDepartmentItems, buildEmployeeBgItem } from './builders';
import { createIdGenerator } from './helpers';
import { createIntervalBackgrounds, getWorkHoursFromSchedule } from './intervalHelpers';

export interface ExtendedTreeNode extends TreeNode {
  isIntervalType?: boolean;
  intervalDuration?: number;
}

export interface BuildTimelineItemsArgs {
  calendarAssignment: any;
  departmentSchedules: Record<string, any[]> | null;
  employeesShifts: any;
  startOfMonth: moment.Moment;
  timelineScale: TimelineScale;
  newShifts: any[];
  updatedShifts: any[];
  deletedShiftIds: string[];
  simulatedDeletedShifts: string[];
  simulatedUpdatedShifts: any[];
  viewMode: 'schedule' | 'timesheet';
  employeesTimesheet: Record<string, Record<string, number>> | null;
  visibleEntities: { departmentSchedule: boolean; absences: boolean; plannedShifts: boolean; manualShifts: boolean } & Record<string, boolean>;
  employeesAbsences: Record<string, Record<string, PersonAbsence[]>>;
  newAbsences: PersonAbsence[];
  updatedAbsences: PersonAbsence[];
  deletedAbsenceKeys: any[];
  buildEmployeeScheduleItems: Function;
  buildTimesheetShiftItems: Function;
  buildTaskItemsMonthScale: Function;
  taskItems: TimelineItem[];
  DEPARTMENT_SCHEDULE_GROUP_ID: number;
  selectedDepartmentFilter: ExtendedTreeNode | null
}

export function buildTimelineItems(args: BuildTimelineItemsArgs): TimelineItem[] {
  const {
    calendarAssignment,
    departmentSchedules,
    employeesShifts,
    startOfMonth,
    timelineScale,
    newShifts,
    updatedShifts,
    deletedShiftIds,
    simulatedDeletedShifts,
    simulatedUpdatedShifts,
    viewMode,
    employeesTimesheet,
    visibleEntities,
    employeesAbsences,
    newAbsences,
    updatedAbsences,
    deletedAbsenceKeys,
    buildEmployeeScheduleItems,
    buildTimesheetShiftItems,
    buildTaskItemsMonthScale,
    taskItems,
    DEPARTMENT_SCHEDULE_GROUP_ID,
    selectedDepartmentFilter
  } = args;

  if (!calendarAssignment && !departmentSchedules) return [];
  const getNextId = createIdGenerator(1);
  const isIntervalType = selectedDepartmentFilter?.isIntervalType || false;
  const intervalDuration = selectedDepartmentFilter?.intervalDuration || 1;

  const daysInMonth = startOfMonth.daysInMonth();
  const departmentItems: TimelineItem[] = [];
  const departmentBgItems: TimelineItem[] = [];
  const employeeBgItems: TimelineItem[] = [];
  let employeeScheduleItems: TimelineItem[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const dayMoment = startOfMonth.clone().add(day - 1, 'days');
    const dateKey = dayMoment.format('YYYY-MM-DD');
    const schedules = (departmentSchedules?.[dateKey] || []) as any[];
    departmentItems.push(
      ...buildDepartmentItems(
        dayMoment,
        schedules,
        timelineScale,
        DEPARTMENT_SCHEDULE_GROUP_ID,
        () => `${Math.random().toString(36).slice(2)}`
      )
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayMoment = startOfMonth.clone().add(day - 1, 'days');
    const dateKey = dayMoment.format('YYYY-MM-DD');
    const dayType = calendarAssignment?.calendar?.[dateKey]?.type;
    const schedules = departmentSchedules?.[dateKey] || [];
    
    if (departmentSchedules) {
      departmentBgItems.push(
        buildDepartmentBgItem(dayMoment, dayType, DEPARTMENT_SCHEDULE_GROUP_ID, getNextId()) as any
      );
    }
    
    if (calendarAssignment?.employees && calendarAssignment.calendar) {
      let workStartMinutes: number | null = null;
      let workEndMinutes: number | null = null;
      
      if (schedules.length > 0) {
        const workHours = getWorkHoursFromSchedule(schedules[0]);
        if (workHours) {
          workStartMinutes = workHours.start;
          workEndMinutes = workHours.end;
        }
      }
      
      for (const emp of calendarAssignment.employees) {
        const shouldShowIntervals = isIntervalType && 
                                    timelineScale === TimelineScale.DAY && 
                                    workStartMinutes !== null && 
                                    workEndMinutes !== null;
        
        if (shouldShowIntervals) {
          const intervalBgItems = createIntervalBackgrounds(
            emp.id,
            dayMoment,
            dateKey,
            workStartMinutes as number,
            workEndMinutes as number,
            intervalDuration
          );
          employeeBgItems.push(...intervalBgItems);
        } else {
          employeeBgItems.push(buildEmployeeBgItem(emp.id, dayMoment, dayType) as any);
        }
      }
    }
  }

  if (viewMode === 'schedule' && employeesShifts) {
    employeeScheduleItems = buildEmployeeScheduleItems(
      employeesShifts,
      newShifts,
      updatedShifts,
      deletedShiftIds,
      simulatedDeletedShifts,
      simulatedUpdatedShifts
    );
  } else if (viewMode === 'timesheet' && employeesTimesheet) {
    employeeScheduleItems = buildTimesheetShiftItems(employeesTimesheet);
  }

  // Absences
  const absenceItems: TimelineItem[] = buildAbsenceItems(
    calendarAssignment,
    employeesAbsences,
    newAbsences,
    updatedAbsences,
    deletedAbsenceKeys,
    timelineScale
  ) as any;

  return [
    ...(visibleEntities.departmentSchedule ? departmentBgItems : []),
    ...employeeBgItems,
    ...(visibleEntities.departmentSchedule ? departmentItems : []),
    ...employeeScheduleItems.filter((item) => {
      if (item.className?.includes('template-shift') && !visibleEntities.plannedShifts) return false;
      if (item.className?.includes('manual-shift') && !visibleEntities.manualShifts) return false;
      return true;
    }),
    ...(visibleEntities.absences ? absenceItems : []),
    ...(timelineScale === TimelineScale.MONTH ? buildTaskItemsMonthScale() : taskItems)
  ];
}